import { createContext, useContext, useCallback, useEffect, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apolloClient, graphqlClient } from "./graphql";
import { graphql } from "@/__generated__";
import type { PayoutProviderEnum } from "@/__generated__/graphql";
import { useHeartbeat } from "@/components/chat/useHeartbeat";
import { retryNetworkFailures } from "@/lib/queryClient";
import { currentPushPlayerId } from "@/lib/onesignal";
import { setSentryUser } from "@/lib/sentry";

// --- GraphQL documents ---

const ME_QUERY = graphql(`
  query Me {
    me {
      id
      emailAddress
      firstName
      lastName
      fullName
      initials
      role
      uiLocale
      avatarUrl
      referralCode
      emailVerified
      isStaff
      studentProfile {
        id
        nativeLanguage { id code name }
        studentLanguages {
          id
          language { id name code }
          level
        }
      }
      tutorProfile {
        id
        profileTitle
        hourlyRateCents
        timezone
        teachingLanguage { id name code }
        residenceCountry
        suggestedPayoutCountry
        payoutsEnabled
        identityVerified
        profileApproved
        stripeAccount {
          id
          status
          chargesEnabled
          payoutsEnabled
          detailsSubmitted
          requirementsCurrentlyDue
          disabledReason
        }
        payoutAccount {
          id
          provider
          status
          payoutsEnabled
          requirementsCurrentlyDue
          disabledReason
          accountSummary
        }
        identityDocument {
          id
          status
          rejectionReason
        }
      }
      tutorApplication {
        id
        status
      }
    }
  }
`);

const SIGN_IN_MUTATION = graphql(`
  mutation SignIn($emailAddress: String!, $password: String!) {
    signIn(input: { emailAddress: $emailAddress, password: $password }) {
      user {
        id
        firstName
        lastName
        fullName
        role
      }
      errors
      twoFactorRequired
      challengeToken
    }
  }
`);

const VERIFY_TWO_FACTOR_MUTATION = graphql(`
  mutation VerifyTwoFactor($challengeToken: String!, $otpCode: String!) {
    verifyTwoFactor(input: { challengeToken: $challengeToken, otpCode: $otpCode }) {
      user {
        id
        firstName
        lastName
        fullName
        role
      }
      errors
    }
  }
`);

const SIGN_UP_MUTATION = graphql(`
  mutation SignUp($emailAddress: String!, $password: String!, $firstName: String!, $lastName: String!, $role: String, $acceptedLegal: Boolean, $marketingOptIn: Boolean, $website: String, $formElapsedMs: Int, $referralCode: String, $uiLocale: String) {
    signUp(input: { emailAddress: $emailAddress, password: $password, firstName: $firstName, lastName: $lastName, role: $role, acceptedLegal: $acceptedLegal, marketingOptIn: $marketingOptIn, website: $website, formElapsedMs: $formElapsedMs, referralCode: $referralCode, uiLocale: $uiLocale }) {
      user {
        id
        firstName
        lastName
        fullName
        role
      }
      errors
      emailPreviouslyUsed
    }
  }
`);

const SIGN_OUT_MUTATION = graphql(`
  mutation SignOut($input: SignOutInput!) {
    signOut(input: $input) {
      success
    }
  }
`);

// --- Types ---

export type StripeAccountStatus =
  | "NOT_STARTED"
  | "PENDING_VERIFICATION"
  | "REQUIREMENTS_DUE"
  | "LIMITED"
  | "READY"
  | "DISABLED";

export interface StripeAccount {
  id: string;
  status: StripeAccountStatus;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirementsCurrentlyDue: string[];
  disabledReason: string | null;
}

// #623 Phase 2: the rail-agnostic payout account (Stripe mirror or Payoneer
// payee). Status values intentionally mirror StripeAccountStatus. The rails
// are the GraphQL schema's own list (#885), so a new one can't be missed here.
export type PayoutProvider = `${PayoutProviderEnum}`;

/**
 * #623 Phase 2b: display-safe description of a Wise payout destination —
 * currency, account type, a 4-character tail. Empty ({}) on the hosted rails,
 * which have their own dashboards for this. Never a payout instrument.
 */
export interface PayoutAccountSummary {
  currency?: string;
  type?: string;
  type_title?: string;
  holder_name?: string;
  hint?: string;
  // Manual bank-transfer rail only: the country of the receiving bank, which
  // the edit form starts from (Payouts::ManualBank::Destination#summary_for).
  bank_country?: string;
}

export interface PayoutAccount {
  id: string;
  provider: PayoutProvider;
  status: StripeAccountStatus;
  payoutsEnabled: boolean;
  requirementsCurrentlyDue: string[];
  disabledReason: string | null;
  accountSummary: PayoutAccountSummary;
}

export type IdentityStatus = "NOT_STARTED" | "PENDING" | "VERIFIED" | "REJECTED";

export interface IdentityDocument {
  id: string;
  status: IdentityStatus;
  rejectionReason: string | null;
}

export interface User {
  id: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  fullName: string;
  initials: string;
  role: "student" | "tutor" | "admin";
  uiLocale: string;
  avatarUrl: string | null;
  referralCode: string | null;
  emailVerified: boolean;
  isStaff: boolean;
  studentProfile: {
    id: string;
    // The student's L1 — what flashcards translate into. Null only for a
    // profile created before onboarding asked (the server falls back to
    // ui_locale, then English).
    nativeLanguage: { id: string; code: string; name: string } | null;
    studentLanguages: {
      id: string;
      language: { id: string; name: string; code: string };
      level: string;
    }[];
  } | null;
  tutorProfile: {
    id: string;
    profileTitle: string | null;
    hourlyRateCents: number;
    timezone: string;
    teachingLanguage: { id: string; name: string; code: string };
    residenceCountry: string | null;
    suggestedPayoutCountry: string | null;
    payoutsEnabled: boolean;
    identityVerified: boolean;
    profileApproved: boolean;
    stripeAccount: StripeAccount | null;
    payoutAccount: PayoutAccount | null;
    identityDocument: IdentityDocument | null;
  } | null;
  tutorApplication: {
    id: string;
    status: string;
  } | null;
}

interface SignInResult {
  user: User | null;
  errors: string[];
  twoFactorRequired: boolean;
  challengeToken: string | null;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  verifyTwoFactor: (challengeToken: string, otpCode: string) => Promise<{ user: User | null; errors: string[] }>;
  signUp: (params: { emailAddress: string; password: string; firstName: string; lastName: string; role?: string; acceptedLegal?: boolean; marketingOptIn?: boolean; website?: string; formElapsedMs?: number; referralCode?: string; uiLocale?: string }) => Promise<{ user: User | null; errors: string[]; emailPreviouslyUsed: boolean }>;
  // `onSignedOut` runs in the same tick as the cache is emptied, so the app
  // moves on (useSignOut navigates) in the same render that learns nobody is
  // signed in, never after a render of a protected page with no user (#1346).
  signOut: (onSignedOut?: () => void) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// How long signIn waits for the account refresh before resolving anyway.
const SIGN_IN_REFRESH_TIMEOUT_MS = 5000;

function atMost<T>(promise: Promise<T>, ms: number): Promise<T | void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// --- Provider ---

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // isPending, not TanStack's isLoading: while a retry waits for the browser
  // to come back online (or the tab to be visible) isLoading is false with no
  // user yet, and ProtectedRoute sent a signed-in tutor to /signin.
  const { data, isPending } = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const result = await graphqlClient.request(ME_QUERY);
      return result.me;
    },
    retry: retryNetworkFailures,
    staleTime: 5 * 60 * 1000,
  });

  const signInMutation = useMutation({
    mutationFn: (vars: { emailAddress: string; password: string }) =>
      graphqlClient.request(SIGN_IN_MUTATION, vars),
    onSuccess: (data) => {
      // Only a sign-in that returns a user creates a session. A 2FA challenge, a
      // wrong password or a locked account does not, so refreshing `me` would be
      // a pointless refetch that returns null, and would hold up their answer.
      // verifyTwoFactor refetches once the second factor actually creates a session.
      if (data.signIn.user) {
        // signIn resolves once the refresh is done: SignIn.tsx then follows
        // ?returnTo= with a full page load, and Safari cancels any request
        // still in flight when one starts. Capped, so a stalled refresh cannot
        // keep the button spinning.
        return atMost(queryClient.invalidateQueries({ queryKey: ["me"] }), SIGN_IN_REFRESH_TIMEOUT_MS);
      }
    },
  });

  const verifyTwoFactorMutation = useMutation({
    mutationFn: (vars: { challengeToken: string; otpCode: string }) =>
      graphqlClient.request(VERIFY_TWO_FACTOR_MUTATION, vars),
    // Capped like signIn: a stalled refresh must not keep the button spinning.
    onSuccess: () => atMost(queryClient.invalidateQueries({ queryKey: ["me"] }), SIGN_IN_REFRESH_TIMEOUT_MS),
  });

  const signUpMutation = useMutation({
    mutationFn: (vars: { emailAddress: string; password: string; firstName: string; lastName: string; role?: string; acceptedLegal?: boolean; marketingOptIn?: boolean }) =>
      graphqlClient.request(SIGN_UP_MUTATION, vars),
    // Capped like signIn: a stalled refresh must not keep the button spinning.
    onSuccess: () => atMost(queryClient.invalidateQueries({ queryKey: ["me"] }), SIGN_IN_REFRESH_TIMEOUT_MS),
  });

  const signOutMutation = useMutation({
    // This browser's push subscription goes with the session, so the next person
    // on a shared device does not get this account's notifications (#609). The
    // account's other devices keep theirs.
    mutationFn: (_onSignedOut?: () => void) =>
      graphqlClient.request(SIGN_OUT_MUTATION, { input: { onesignalPlayerId: currentPushPlayerId() } }),
    onSuccess: async (_data, onSignedOut) => {
      // Whatever was fetched for this account (names, lessons, settings, what it
      // said about news and offers #722, the one-time question #1209) must not
      // render for the next one to sign in here. Everything goes, not key by
      // key. "me" is the one entry kept, set to null: the screens watching it
      // read the empty answer instead of a refetch in flight.
      await queryClient.cancelQueries();
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
      queryClient.setQueryData(["me"], null);
      onSignedOut?.();
      await apolloClient.clearStore();
    },
  });

  // The auth mutations select a partial user (id/name/role); the wider domain
  // `User` type is the established public contract across ~50 call sites, so we
  // map the inferred payloads to it explicitly. The documents themselves are
  // still schema-checked by codegen.
  const signIn = useCallback(async (email: string, password: string) => {
    const result = await signInMutation.mutateAsync({ emailAddress: email, password });
    return result.signIn as SignInResult;
  }, [signInMutation]);

  const verifyTwoFactor = useCallback(async (challengeToken: string, otpCode: string) => {
    const result = await verifyTwoFactorMutation.mutateAsync({ challengeToken, otpCode });
    return result.verifyTwoFactor as { user: User | null; errors: string[] };
  }, [verifyTwoFactorMutation]);

  const signUp = useCallback(async (params: { emailAddress: string; password: string; firstName: string; lastName: string; role?: string; acceptedLegal?: boolean; marketingOptIn?: boolean; website?: string; formElapsedMs?: number; referralCode?: string; uiLocale?: string }) => {
    const result = await signUpMutation.mutateAsync(params);
    return result.signUp as { user: User | null; errors: string[]; emailPreviouslyUsed: boolean };
  }, [signUpMutation]);

  const signOut = useCallback(async (onSignedOut?: () => void) => {
    await signOutMutation.mutateAsync(onSignedOut);
  }, [signOutMutation]);

  const user = (data ?? null) as User | null;

  // Mark the user as online while they have any tab open and visible.
  useHeartbeat(!!user);

  // Sentry events say which account was hit (the id only), none once signed out.
  const userId = user?.id ?? null;
  useEffect(() => {
    setSentryUser(userId ? { id: userId } : null);
  }, [userId]);

  return (
    <AuthContext.Provider value={{ user, isLoading: isPending, isAuthenticated: !!user, signIn, verifyTwoFactor, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

// --- Hook ---

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
